import * as React from "react";
import { Popover } from "./ui/popover";
import type { Request } from "@/types/research.types";
import { resizeTextarea } from "./chat-input.utils";
import {
  useRequestCallbacks,
  useFormCallbacks,
  useChatInputState,
  useDictationControls,
} from "./chat-input-callbacks";
import { ChatInputBody, ChainConfirmDialog } from "./ChatInputWithGalleryParts";

interface ChatInputWithGalleryProps {
  onSendMessage: (message: string) => Promise<void> | void;
  isSendingMessage: boolean;
  showCompassButton?: boolean;
  handleCompassClick?: () => void;
  compassAssetSrc?: string;
  requestType?: "discuss" | "research";
}

// Main component
const ChatInputWithGallery: React.FC<ChatInputWithGalleryProps> = ({
  onSendMessage,
  isSendingMessage,
  showCompassButton = false,
  handleCompassClick,
  compassAssetSrc,
  requestType = "discuss",
}) => {
  const state = useChatInputState(requestType);
  const dictation = useDictationControls(state.setChatMessage);
  const [pendingChainRequest, setPendingChainRequest] = React.useState<Request | null>(null);
  React.useEffect(() => {
    resizeTextarea(state.textareaRef.current);
  }, [state.chatMessage, dictation.isSpeaking]);
  const { handleRequestSelect, handleCommandSelect } = useRequestCallbacks(
    state,
    onSendMessage,
    setPendingChainRequest
  );
  const { handleTextareaChange, handleSendMessage, handleKeyPress } = useFormCallbacks(
    state,
    onSendMessage,
    handleCommandSelect
  );
  const handleInteractOutside = (e: Event) => {
    if (state.textareaRef.current?.contains(e.target as Node) === true) e.preventDefault();
  };

  return (
    <Popover
      open={!!state.activeMenu}
      onOpenChange={(open) => {
        if (!open) {
          state.setActiveMenu(null);
          state.textareaRef.current?.focus();
        }
      }}
    >
      <div className={`flex items-end gap-2 mt-3 ${isSendingMessage ? "cursor-not-allowed" : ""}`}>
        <ChatInputBody
          state={state}
          requestType={requestType}
          isSendingMessage={isSendingMessage}
          showCompassButton={showCompassButton}
          handleCompassClick={handleCompassClick}
          compassAssetSrc={compassAssetSrc}
          isRecording={dictation.isRecording}
          isMicroLoading={dictation.isMicroLoading}
          isSpeaking={dictation.isSpeaking}
          isConverting={dictation.isConverting}
          startDictation={dictation.startDictation}
          handleRequestSelect={handleRequestSelect}
          handleTextareaChange={handleTextareaChange}
          handleKeyPress={handleKeyPress}
          handleCommandSelect={handleCommandSelect}
          handleSendMessage={handleSendMessage}
          handleInteractOutside={handleInteractOutside}
          currentRequests={state.currentRequests}
        />
      </div>
      <ChainConfirmDialog
        pendingChainRequest={pendingChainRequest}
        onClose={() => setPendingChainRequest(null)}
        handleRequestSelect={handleRequestSelect}
      />
    </Popover>
  );
};

export default ChatInputWithGallery;
